import React, { useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "./ui/sheet";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { AI } from "../constants/testIds";
import { Sparkles, Send } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AIAnalysisPanel = ({ open, onClose, selectedProperties }) => {
  const [question, setQuestion] = useState("");
  const [response, setResponse] = useState("");
  const [loading, setLoading] = useState(false);

  const handleAnalyze = async () => {
    if (selectedProperties.length === 0) {
      toast.error("Lütfen en az bir gayrimenkul seçin");
      return;
    }

    setLoading(true);
    setResponse("");

    try {
      const propertyIds = selectedProperties.map((p) => p.id);
      const result = await fetch(`${API_URL}/api/ai/analyze`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          property_ids: propertyIds,
          question: question || null,
        }),
      });

      const reader = result.body.getReader();
      const decoder = new TextDecoder();

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split("\n");

        for (const line of lines) {
          if (line.startsWith("data: ")) {
            const data = line.slice(6);
            if (data === "[DONE]") {
              break;
            }
            setResponse((prev) => prev + data);
          }
        }
      }

      toast.success("Analiz tamamlandı");
    } catch (error) {
      console.error("AI analysis error:", error);
      toast.error("Analiz sırasında bir hata oluştu");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent
        data-testid={AI.analysisPanel}
        className="w-full sm:max-w-2xl bg-white dark:bg-[#18181B] border-[#E4E4E7] dark:border-[#27272A] overflow-y-auto"
      >
        <SheetHeader>
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#F59E0B] to-[#D97706] flex items-center justify-center mb-4">
            <Sparkles className="w-6 h-6 text-white" />
          </div>
          <SheetTitle className="text-2xl font-bold text-[#0A0A0A] dark:text-[#FAFAFA]">
            AI Yatırım Analizi
          </SheetTitle>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          <div>
            <h3 className="text-xs uppercase tracking-[0.2em] font-bold text-[#52525B] dark:text-[#A1A1AA] mb-3">
              Seçili Gayrimenkuller ({selectedProperties.length})
            </h3>
            <div className="space-y-2">
              {selectedProperties.map((property) => (
                <div
                  key={property.id}
                  className="p-3 bg-[#F9FAFB] dark:bg-[#09090B] border border-[#E4E4E7] dark:border-[#27272A] rounded text-sm"
                >
                  <p className="font-semibold text-[#0A0A0A] dark:text-[#FAFAFA]">
                    {property.title}
                  </p>
                  <p className="text-xs text-[#52525B] dark:text-[#A1A1AA] mt-1">
                    {property.location} • {property.price.toLocaleString("tr-TR")} TL
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h3 className="text-xs uppercase tracking-[0.2em] font-bold text-[#52525B] dark:text-[#A1A1AA] mb-2">
              Özel Soru (Opsiyonel)
            </h3>
            <Textarea
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="Örn: Hangi gayrimenkul daha hızlı değer kazanacak?"
              rows={3}
              className="bg-white dark:bg-[#09090B] border-[#E4E4E7] dark:border-[#27272A]"
            />
          </div>

          <Button
            onClick={handleAnalyze}
            disabled={loading || selectedProperties.length === 0}
            className="w-full bg-[#F59E0B] hover:bg-[#D97706] text-white"
          >
            {loading ? (
              <>
                <Sparkles className="w-4 h-4 mr-2 animate-pulse" />
                Analiz Ediliyor...
              </>
            ) : (
              <>
                <Send className="w-4 h-4 mr-2" />
                Analiz Et
              </>
            )}
          </Button>

          {response && (
            <div>
              <h3 className="text-xs uppercase tracking-[0.2em] font-bold text-[#52525B] dark:text-[#A1A1AA] mb-3">
                AI Tavsiyesi
              </h3>
              <div
                data-testid={AI.responseArea}
                className="p-4 bg-gradient-to-br from-[#F9FAFB] to-[#FFFFFF] dark:from-[#09090B] dark:to-[#18181B] border border-[#E4E4E7] dark:border-[#27272A] rounded-lg"
              >
                <p className="text-base text-[#0A0A0A] dark:text-[#FAFAFA] leading-relaxed whitespace-pre-wrap">
                  {response}
                </p>
              </div>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default AIAnalysisPanel;